import { AsyncPipe, DecimalPipe, NgClass, NgStyle } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonComponent,
	ButtonIconComponent,
	IconComponent,
	INavHelp,
	LinkComponent,
	NavHelpComponent,
	PwcsLayoutComponent,
	QrComponent,
	ScanComponent,
	SpinnerComponent,
	SwitchComponent,
	TileComponent,
	TopnavComponent,
	WatermarkComponent,
} from '@tecalisdev/infinity';
import { TAlertStatus } from '@tecalisdev/infinity/lib/interfaces/alert';
import { interval, Subject, Subscription, takeUntil, timer } from 'rxjs';
import { ImageHelperService } from 'src/app/core/helpers/image-helper.service';
import { ProgressService } from 'src/app/core/helpers/progress.service';
import { IAlert } from 'src/app/core/models/alert.models';
import { ILoading } from 'src/app/core/models/loading.models';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { AuthService } from 'src/app/core/services/auth.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { ModalChangeDeviceComponent } from 'src/app/shared/components/modal-change-device/modal-change-device.component';
import { PwcsInlineSvgComponent } from 'src/app/shared/components/pwcs-inline-svg/pwcs-inline-svg.component';
import { ModalHelpDocumentComponent } from 'src/app/shared/components/modal-help-document/modal-help-document.component';
import { ModalHelpNoCameraPermissionsComponent } from 'src/app/shared/components/modal-help-no-camera-permissions/modal-help-no-camera-permissions.component';
import { ModalScanQrComponent } from 'src/app/shared/components/modal-scan-qr/modal-scan-qr.component';
import { AudioService } from 'src/app/shared/services/audio.service';
import { CameraService } from 'src/app/shared/services/camera.service';
import { CameraPermissionsTimerService } from 'src/app/shared/services/camera-permissions-timer.service';
import { waitForVideoPreviewReady } from 'src/app/shared/utils/wait-for-video-preview-ready.util';
import { onCameraPreviewSwitchStarted } from 'src/app/shared/utils/camera-preview-switch.util';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { FullscreenService } from 'src/app/shared/services/fullscreen.service';
import { LanguagePickerUiService } from 'src/app/shared/services/language-picker-ui.service';
import { NavHelpService } from 'src/app/shared/services/nav-help.service';
import { TextToSpeechService } from 'src/app/shared/services/text-to-speech.service';
import { environment } from 'src/environments/environment';
import { IDocumentTestPhoto, IOcrCannyConf, OCR_FINISH_TIME, OCR_WARNING_TIME } from '../../models/ocr';
import {
	OcrLog,
	IFinishOperation,
	IOcrCannyOperation,
	IOcrSetting,
	IOperation,
	IPageWarning,
	TEST_STATUS,
	OPERATION_STATUS,
	OPERATION_TYPE,
	PAGE_WARNING,
	quality,
	TestStatus,
} from '../../models/user-identification';
import { PositionDocumentService } from '../../services/ocr/position-document.service';
import { ProcessHelperService } from '../../services/process-helper.service';
import { ocrBackConfWithOutModels, ocrFrontConfWithOutModels } from '../ocr/ocr-conf';

@Component({
	selector: 'app-ocr-canny',
	imports: [
		ReactiveFormsModule,
		TranslatePipe,
		DecimalPipe,
		AsyncPipe,
		NgClass,
		NgStyle,
		AlertComponent,
		ButtonComponent,
		ButtonIconComponent,
		IconComponent,
		LinkComponent,
		NavHelpComponent,
		PwcsLayoutComponent,
		QrComponent,
		ScanComponent,
		SpinnerComponent,
		SwitchComponent,
		TileComponent,
		TopnavComponent,
		WatermarkComponent,
		ModalScanQrComponent,
		ModalHelpDocumentComponent,
		ModalHelpNoCameraPermissionsComponent,
		ModalChangeDeviceComponent,
		PwcsInlineSvgComponent,
	],
	templateUrl: './ocr-canny.component.html',
	styleUrls: ['./ocr-canny.component.scss'],
})
export class OcrCannyComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() operations: IOperation[];
	@Input()
	get operation(): IOcrCannyOperation {
		return this._operation;
	}
	set operation(value: IOcrCannyOperation) {
		this._operation = value;
		this.operation.config.iframe
			? this.loadingService.showIframe(this.trans.instant('GENERAL.LOADING.CAMERA_INIT'))
			: this.loadingService.show(this.trans.instant('GENERAL.LOADING.CAMERA_INIT'));
		this.activatePreviewLoading('GENERAL.LOADING.CAMERA_INIT');
		this.ocrCannyConf = this.setOcrSetting(value.config.setting);
	}

	private _operation: IOcrCannyOperation;
	private languagePickerUi = inject(LanguagePickerUiService);

	// General
	public env = environment;
	public loading: ILoading = {
		show: false,
		text: '',
	};
	public navsHelp: Array<INavHelp> = this.navHelpService.getNavsHelp();
	public navsHelpTutorial: Array<INavHelp> = this.navHelpService.getTutorialNavsHelp();
	public isVisibleScan: boolean = false;
	public isVisibleHelp: boolean = false;
	public isVisibleHelpNoPermissionsCamera: boolean = false;
	public isVisibleChangeDevice: boolean = false;
	public permissionsCamera: boolean = true;
	private cameraPermissionsReady = false;
	private cameraPermissionsTimerService = inject(CameraPermissionsTimerService);
	public pageWarning: IPageWarning = { show: false, type: null };
	public PAGE_WARNING = PAGE_WARNING;
	public operationType = OPERATION_TYPE;
	public openQR: boolean = false;

	// Document check
	public match: boolean = false;
	public ocrCannyConf: IOcrCannyConf;
	private subscriptionTimers: Subscription = new Subscription();
	private runningMode: 'IMAGE' | 'VIDEO' = 'IMAGE';
	private fullDataMatch: Array<boolean> = [];
	public OcrLog: any = OcrLog;
	public LogType: any = LogType;

	// Controles
	public formRect: FormControl = new FormControl(false);
	public formStats: FormControl = new FormControl(false);
	public totalTime: number = 0;
	public warningTime: number = 0;
	public controls: any = {
		sharpness: 0,
		minSharpness: 0,
	};
	public testMode: boolean = false;

	// Configuración de la prueba
	private subsTotalTime: Subscription;
	private subsWarningTime: Subscription;

	// Camara
	public video!: HTMLVideoElement;
	public canvas!: HTMLCanvasElement;
	public ctx!: CanvasRenderingContext2D;
	public stream: MediaStream;
	private animationFrame: number;
	// eslint-disable-next-line @typescript-eslint/ban-types
	private webcamRunning: Boolean = false;
	private lastVideoTime: number = -1;
	public allCameras: Array<MediaDeviceInfo> = [];
	private destroy$ = new Subject<void>();
	private readonly onDeviceChangeRefreshCameras = (): void => {
		this.cameraService.getAllCameras();
	};

	constructor(
		public processHelperService: ProcessHelperService,
		public cloudLogsService: CloudLogsService,
		public progressService: ProgressService,
		public audioService: AudioService,
		public positionDocumentService: PositionDocumentService,
		public designService: DesignService,
		private trans: TranslateService,
		private loadingService: LoadingService,
		private route: ActivatedRoute,
		private fullscreenService: FullscreenService,
		private imageHelperService: ImageHelperService,
		private tts: TextToSpeechService,
		private cameraService: CameraService,
		public auth: AuthService,
		private navHelpService: NavHelpService,
	) {
		this.route.queryParams.subscribe((params: any) => {
			this.testMode = Boolean(params.testmode);
		});
	}

	ngOnInit(): void {
		this.cameraService.allCameras$.pipe(takeUntil(this.destroy$)).subscribe((cameras: MediaDeviceInfo[]) => {
			this.applySwitchableCamerasFromService(cameras);
		});
		navigator.mediaDevices.addEventListener('devicechange', this.onDeviceChangeRefreshCameras);
	}

	ngAfterViewInit(): void {
		this.checkCameraPermissions();
	}

	private startWebcam(): void {
		if (this.permissionsCamera) {
			this.video = document.getElementById('video') as HTMLVideoElement;
			this.canvas = document.getElementById('controlCanvas') as HTMLCanvasElement;
			this.ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;

			if (this.hasGetUserMedia()) {
				this.enableCam();
			}
		}
	}

	public nextOperation(status: OPERATION_STATUS): void {
		this.operation.completed = true;
		const found = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
		if (found) {
			(found as IFinishOperation).config.status = status;
		}
	}

	/*
	 * Service enviar datos
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private async sendResult(valid: boolean, status: TEST_STATUS = TEST_STATUS.OK): Promise<boolean> {
		return new Promise((resolve) => {
			timer(250).subscribe(() => {
				const prueba: 'ok' | 'ko' | 'cancel' = 'ok' as 'ok' | 'ko' | 'cancel';
				const requiredFront: boolean = true;
				if (prueba === 'ok') {
					const index = this.operations.findIndex((op: IOperation) => op.type === OPERATION_TYPE.OCR_CANNY);
					if (requiredFront && index > -1) {
						console.log('Se actualiza la operación OCR CANNY con el resultado de la validación');
					}
					timer(OCR_FINISH_TIME * 1000).subscribe(() => {
						this.nextOperation(valid ? OPERATION_STATUS.OK : OPERATION_STATUS.KO);
					});
					resolve(true);
				}
				if (prueba === 'ko') {
					resolve(false);
				}
				if (prueba === 'cancel') {
					this.pageWarning.show = true;
					this.pageWarning.type = PAGE_WARNING.SERVER_ERROR;
					this.loading.show = false;
					resolve(false);
				}
			});
		});
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public retryAllProcess(observations: string): void {
		this.startTotalTime();
		this.generalTries();
		this.pageWarning.show = false;
		this.pageWarning.type = null;
		this.stopWarningTime();
	}

	/*
	 * Gestión de los textos
	 */
	public getContentAlert(): IAlert {
		if (this.ocrCannyConf) {
			if (
				!this.ocrCannyConf.general.complete &&
				this.ocrCannyConf.general.status !== TestStatus.OnFail &&
				this.ocrCannyConf.general.status !== TestStatus.OnWarning
			) {
				// Documento
				if (!this.ocrCannyConf.document.complete) {
					return {
						label: this.ocrCannyConf.document.messages[this.ocrCannyConf.document.status],
						status: this.getAlertStatus(this.ocrCannyConf.document.status),
					};
				} else {
					return {
						label: this.ocrCannyConf.general.messages[this.ocrCannyConf.general.status],
						status: this.getAlertStatus(this.ocrCannyConf.general.status),
					};
				}
			}
			// Proceso terminado correctamente
			return {
				label: this.ocrCannyConf.general.messages[this.ocrCannyConf.general.status],
				status: this.getAlertStatus(this.ocrCannyConf.general.status),
			};
		}
		return { label: '', status: 'default' };
	}

	private getAlertStatus(status: TestStatus): TAlertStatus {
		const actions = {
			[TestStatus.OnInit]: () => 'default',
			[TestStatus.OnComplete]: () => 'success',
			[TestStatus.OnWarning]: () => 'warning',
			[TestStatus.OnFail]: () => 'error',
		};

		return actions[status]() as TAlertStatus;
	}

	public getSubtitle(): string {
		if (
			this.loading.show ||
			!this.permissionsCamera ||
			this.ocrCannyConf?.general.status === TestStatus.OnFail ||
			this.ocrCannyConf?.general.status === TestStatus.OnComplete ||
			this.pageWarning.type === PAGE_WARNING.VERIFICATION_FAIL ||
			this.pageWarning.type === PAGE_WARNING.SERVER_ERROR ||
			this.pageWarning.type === PAGE_WARNING.CAMERA_ERROR
		) {
			return '';
		}
		if (this.pageWarning.type === PAGE_WARNING.HELP) {
			return this.trans.instant('OCR.BACK.HELP.DESCRIPTION');
		}
		if (this.operation?.config.tutorial) {
			return this.operation.type === OPERATION_TYPE.OCR_CANNY
				? this.trans.instant('OCR.BACK.TUTORIAL_TITLE')
				: this.trans.instant('OCR.FRONT.TUTORIAL_TITLE');
		}
		if (this.loading.show || this.pageWarning.show) {
			return '';
		}
		if (!this.operation?.config.tutorial) {
			return this.operation.type === OPERATION_TYPE.OCR_CANNY
				? this.trans.instant('OCR.BACK.TITLE')
				: this.trans.instant('OCR.FRONT.TITLE');
		}
		return '';
	}

	/*
	 * Tutorial
	 */
	public finishTutorial(): void {
		this.operation.config.tutorial = false;
		this.stopWarningTime();
		this.audioService.preloadOcrMrzAudios();
		if (!this.operation.config.iframe) {
			this.fullscreenService.enterFullscreen();
		}
	}

	/*
	 * Controles de la cámara
	 */
	private enableCam(): void {
		if (this.webcamRunning) {
			this.webcamRunning = false;
		} else {
			this.webcamRunning = true;
		}

		// getUsermedia parameters.
		const constraints: MediaStreamConstraints = {
			video: {
				facingMode: {
					ideal: 'environment',
				},
				width: { ideal: 800 },
				height: { ideal: 600 },
				aspectRatio: 4 / 3,
			},
		};
		this.activatePreviewLoading('GENERAL.LOADING.CAMERA_STARTING');

		// Activate the webcam stream.
		navigator.mediaDevices
			.getUserMedia(constraints)
			.then(async (stream: MediaStream) => {
				this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
				this.stream = stream;
				this.permissionsCamera = true;
				if (!this.cameraPermissionsReady) {
					this.onPermissionsGranted();
				} else {
					this.resetTestClocksAfterPermissionsGranted();
					this.resumeTestTimersIfApplicable();
				}
				this.cameraService.getAllCameras();
				this.video.srcObject = this.stream;
				this.video.addEventListener('loadeddata', this.predictWebcam);
				await waitForVideoPreviewReady(this.video);
				this.hidePreviewLoading();
			})
			.catch((err) => {
				this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
				console.log(err);
				this.hidePreviewLoading();
				this.cloudLogsService.sendCloudLogs(
					{ getUserMedia: `Error: init camera mediaDevices.getUserMedia - ${err?.toString()}` },
					OcrLog.PRELOAD,
					LogType.Error,
				);
				if (err?.name === 'NotAllowedError') {
					this.permissionsCamera = false;
					this.onPermissionsDenied();
				} else {
					this.pageWarning.show = true;
					this.pageWarning.type = PAGE_WARNING.CAMERA_ERROR;
					this.stopTotalTime();
					this.startWarningTime();
				}
			});
	}

	private canContinueCameraFrameLoop(): boolean {
		return !!this.video && this.permissionsCamera && this.canRunTestTimers() && this.webcamRunning === true;
	}

	private schedulePredictWebcamFrame(): void {
		if (!this.canContinueCameraFrameLoop()) return;
		this.animationFrame = window.requestAnimationFrame(this.predictWebcam);
	}

	private stopWebcamFrameLoop(): void {
		this.webcamRunning = false;
		if (this.animationFrame) {
			cancelAnimationFrame(this.animationFrame);
			this.animationFrame = 0;
		}
		if (this.video) {
			this.video.removeEventListener('loadeddata', this.predictWebcam);
		}
	}

	private predictWebcam = async () => {
		if (!this.canContinueCameraFrameLoop()) {
			return;
		}
		this.canvas.width = JSON.parse(JSON.stringify(this.video.videoWidth));
		this.canvas.height = JSON.parse(JSON.stringify(this.video.videoHeight));

		// Now let's start detecting the stream.
		if (this.runningMode === 'IMAGE') {
			this.runningMode = 'VIDEO';
		}

		if (this.lastVideoTime !== this.video.currentTime) {
			this.lastVideoTime = this.video.currentTime;
		}
		if (
			this.ocrCannyConf?.general &&
			this.ocrCannyConf?.general?.status !== TestStatus.OnComplete &&
			this.ocrCannyConf?.general?.status !== TestStatus.OnFail &&
			!this.loading.show &&
			!this.operation.config.tutorial &&
			!this.pageWarning.show
		) {
			this.documentCheckCycle();
		}

		this.schedulePredictWebcamFrame();
	};

	private async documentCheckCycle(): Promise<void> {
		if (!this.canRunTestTimers()) return;
		// Iniciamos por primera vez la lectura del documento
		if (this.ocrCannyConf.general && !this.ocrCannyConf.general.startAt) {
			this.startTest();
			this.ocrCannyConf.general.startAt = new Date().toISOString();
		}
		// Lectura del documento general ha fallado
		if (this.ocrCannyConf.general.status === TestStatus.OnFail) {
			if (!this.ocrCannyConf.general.finishAt) {
				this.controls = {
					...this.controls,
					lifeTestId: 'Final',
				};
				this.ocrCannyConf.general.finishAt = new Date().toISOString();
			}
			return;
		}
		// Encuadrar documento
		if (!this.ocrCannyConf.document.complete) {
			this.ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
			this.positionDocumentService.setDocumentPoints(this.imageHelperService.getDocumentRect(this.canvas));
			this.isMatch();
			this.documentValidation();
			return;
		}
		// Últimas validaciones
		if (!this.ocrCannyConf.general.finishAt && !this.ocrCannyConf.general.controlValidation) {
			this.finishValidation();
			return;
		}
	}

	private async documentValidation(): Promise<void> {
		// Seteamos el tiempo de inicio de la FASE 1
		if (!this.ocrCannyConf.document.startAt) {
			this.ocrCannyConf.document.startAt = new Date().toISOString();
		}
		// FASE 1: Estado OnWarning
		if (this.ocrCannyConf.document.status === TestStatus.OnWarning) {
			const startDate = new Date(this.ocrCannyConf.document.startAt);
			startDate.setSeconds(startDate.getSeconds() + this.ocrCannyConf.document.maxTime);
			// Revisamos si ha pasado el tiempo de la FASE 1 para pasar a estado OnFail
			if (startDate < new Date()) {
				this.ocrCannyConf.document.status = TestStatus.OnFail;
				// Revisamos si tiene más intentos y volvemos al estado OnWarning o pasamos al estado OnFail todo el proceso
				this.ocrCannyConf.document.countTries++;
				if (this.ocrCannyConf.document.countTries > this.ocrCannyConf.document.tries) {
					if (this.ocrCannyConf.general.countTries + 1 > this.ocrCannyConf.general.tries) {
						this.generalTries();
					} else {
						this.pageWarning.show = true;
						this.pageWarning.type = PAGE_WARNING.HELP;
						this.stopTotalTime();
						this.startWarningTime();
					}
				} else {
					this.subscriptionTimers.add(
						timer(5000).subscribe(() => {
							this.ocrCannyConf.document.startAt = '';
							this.ocrCannyConf.document.status = TestStatus.OnInit;
						}),
					);
				}
			}
		}
		// FASE 1: Estado OnInit
		if (this.ocrCannyConf.document.status === TestStatus.OnInit) {
			const startDate = new Date(this.ocrCannyConf.document.startAt);
			startDate.setSeconds(startDate.getSeconds() + this.ocrCannyConf.document.maxTime / 2);
			// Revisamos si ha pasado la mitad del tiempo de la FASE 1 para pasar a estado OnWarning
			if (startDate < new Date()) {
				this.ocrCannyConf.document.status = TestStatus.OnWarning;
			}
		}
		// FASE 1: Estado OnFail
		if (this.ocrCannyConf.document.status === TestStatus.OnFail) {
			return;
		}
		if (this.match && this.ocrCannyConf.document.photos.length === 2) {
			if ((await this.isDocumentValid()) && (await this.sendResult(true))) {
				this.ocrCannyConf.document.complete = true;
			} else {
				this.ocrCannyConf.document.photos = [];
			}
		}
	}

	private async isDocumentValid(): Promise<boolean> {
		if (this.ocrCannyConf.document.photos.length) {
			const sharpness = await this.arePhotosDocumentSharpnessLapacian(this.ocrCannyConf.document.photos);
			this.controls = {
				...this.controls,
				sharpness: sharpness,
			};
			if (sharpness) {
				return sharpness >= this.ocrCannyConf.document.minSharpness;
			}
		}
		return false;
	}

	private async arePhotosDocumentSharpnessLapacian(photos: IDocumentTestPhoto[]): Promise<number> {
		if (Array.isArray(photos) && photos?.length) {
			await this.waitLoadingPhotos(photos);

			const canvas1 = document.getElementById('canvasAux1') as HTMLCanvasElement;
			const ctx1 = canvas1.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
			const canvas2 = document.getElementById('canvasAux2') as HTMLCanvasElement;
			const ctx2 = canvas2.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;

			canvas1.width = this.canvas.width;
			canvas1.height = this.canvas.height;
			canvas2.width = this.canvas.width;
			canvas2.height = this.canvas.height;
			ctx1.drawImage(
				document.getElementById('imageAux1') as HTMLImageElement,
				0,
				0,
				canvas1.width,
				canvas1.height,
			);
			ctx2.drawImage(
				document.getElementById('imageAux2') as HTMLImageElement,
				0,
				0,
				canvas2.width,
				canvas2.height,
			);
			const imageData1 = ctx1.getImageData(0, 0, canvas1.width, canvas1.height);
			const imageData2 = ctx2.getImageData(0, 0, canvas2.width, canvas2.height);
			const sharpness: number[] = [
				this.imageHelperService.getLaplacianFilterSharpness(imageData1),
				this.imageHelperService.getLaplacianFilterSharpness(imageData2),
			];
			this.controls = {
				...this.controls,
				sharpness: sharpness.toString(),
				minSharpness: this.ocrCannyConf.document.minSharpness,
			};
			photos.forEach((photo: IDocumentTestPhoto, index: number) => {
				photo.lapacian = sharpness[index];
			});
			return Math.max(...photos.map((photo: IDocumentTestPhoto) => photo.lapacian));
		}
		return 0;
	}

	private waitLoadingPhotos(photos: Array<IDocumentTestPhoto>): Promise<boolean> {
		if (photos.length) {
			if (photos[0]?.img) (document.getElementById('imageAux1') as HTMLImageElement).src = photos[0].img;
			if (photos[1]?.img) (document.getElementById('imageAux2') as HTMLImageElement).src = photos[1].img;
		}
		return new Promise((resolve) => {
			timer(250).subscribe(() => {
				resolve(true);
			});
		});
	}

	// Validaciones finales
	private finishValidation(): void {
		return;
		this.loading = {
			show: true,
			text: this.trans.instant('GENERAL.LOADING.LIFE_CHECK_VALIDATION'),
		};
		this.ocrCannyConf.general.controlValidation = true;
		// eslint-disable-next-line no-constant-condition
		if (true) {
			this.controls = {
				...this.controls,
				lifeTestId: 'Final',
			};
			this.ocrCannyConf.general.status = TestStatus.OnComplete;
			this.ocrCannyConf.general.complete = true;
			this.sendResult(true);
		} else {
			this.generalTries();
		}
	}

	// Evaluamos el número de intentos que lleva el usuario para ver si repetimos la prueba o la marcamos como incompleta
	private generalTries(): void {
		this.loading = {
			show: true,
			text: this.trans.instant('GENERAL.LOADING.LIFE_CHECK_VALIDATION'),
		};
		this.ocrCannyConf.general.countTries++;
		if (this.ocrCannyConf.general.countTries > this.ocrCannyConf.general.tries) {
			this.ocrCannyConf.general.status = TestStatus.OnFail;
			this.sendResult(false, TEST_STATUS.KO_MAXTRIES);
		} else {
			this.loading.show = false;
			this.ocrCannyConf.general.finishAt = new Date().toISOString();
			this.ocrCannyConf.general.status = TestStatus.OnWarning;
			this.repeatAllProcess();
		}
	}

	// Repetimos todo el proceso
	private repeatAllProcess(): void {
		if (this.ocrCannyConf.general.status === TestStatus.OnWarning) {
			this.resetTimers();
			this.ocrCannyConf = {
				...this.ocrCannyConf,
				document: {
					...this.ocrCannyConf.document,
					status: TestStatus.OnInit,
					complete: false,
					startAt: '',
					finishAt: '',
					countTries: 1,
				},
				general: {
					...this.ocrCannyConf.general,
					status: TestStatus.OnInit,
					controlValidation: false,
				},
			};
		}
	}

	public checkPermissions(): void {
		this.checkCameraPermissions();
	}

	private checkCameraPermissions(): void {
		const seq = this.cameraService.beginPermissionCheck();
		// Arrancamos el reloj justo al iniciar la petición de permisos: así el KO por timeout
		// también cubre el caso en que el usuario no responde al prompt del navegador.
		this.cameraPermissionsTimerService.startWait(() => this.onCameraPermissionsTimeout());
		this.operation.config.iframe
			? this.loadingService.showIframe(this.trans.instant('GENERAL.LOADING.PERMISSIONS'))
			: this.loadingService.show(this.trans.instant('GENERAL.LOADING.PERMISSIONS'));
		const constraints: MediaStreamConstraints = {
			video: {
				facingMode: { ideal: 'environment' },
				width: { ideal: 800 },
				height: { ideal: 600 },
				aspectRatio: 4 / 3,
			},
		};
		this.cameraService
			.resolveHasCameraPermission(constraints)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (hasPermission: boolean) => this.finishPermissionCheck(seq, hasPermission),
				error: () => this.finishPermissionCheck(seq, false),
			});
	}

	private finishPermissionCheck(seq: number, hasPermission: boolean): void {
		if (!this.cameraService.isPermissionCheckCurrent(seq)) {
			return;
		}
		if (hasPermission) {
			this.permissionsCamera = true;
			this.onPermissionsGranted();
			this.activatePreviewLoading('GENERAL.LOADING.CAMERA_STARTING');
			setTimeout(() => this.startWebcam(), 500);
		} else {
			this.permissionsCamera = false;
			this.onPermissionsDenied();
			this.hidePreviewLoading();
		}
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
	}

	private canRunTestTimers(): boolean {
		return this.permissionsCamera && this.cameraPermissionsReady;
	}

	private onPermissionsDenied(): void {
		this.stopWebcamFrameLoop();
		this.cameraPermissionsReady = false;
		this.cameraService.releaseActiveStream();
		this.stopTotalTime();
		this.stopWarningTime();
		this.cameraPermissionsTimerService.startWait(() => this.onCameraPermissionsTimeout());
	}

	private onPermissionsGranted(): void {
		this.cameraPermissionsTimerService.stopWait();
		this.cameraPermissionsReady = true;
		this.resetTestClocksAfterPermissionsGranted();
		this.resumeTestTimersIfApplicable();
	}

	private resetTestClocksAfterPermissionsGranted(): void {
		if (this.ocrCannyConf?.general) {
			this.ocrCannyConf.general.startAt = '';
		}
		if (this.ocrCannyConf?.document && !this.ocrCannyConf.document.complete) {
			this.ocrCannyConf.document.startAt = '';
		}
		this.stopTotalTime();
		this.totalTime = 0;
		this.stopWarningTime();
	}

	private resumeTestTimersIfApplicable(): void {
		if (!this.pageWarning.show) {
			this.startTotalTime();
		}
	}

	private onCameraPermissionsTimeout(): void {
		// `cameraPermissionsReady` solo pasa a true en onPermissionsGranted(): si seguimos aquí,
		// el usuario no ha concedido permisos (haya respondido NO o no haya respondido al prompt).
		if (this.cameraPermissionsReady) {
			return;
		}
		void this.sendResult(false, TEST_STATUS.KO_PERMISSION);
	}

	private hasGetUserMedia(): boolean {
		const result: boolean = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
		if (!result) {
			this.pageWarning.show = true;
			this.pageWarning.type = PAGE_WARNING.CAMERA_ERROR;
			this.cloudLogsService.sendCloudLogs(
				{ getUserMedia: 'getUserMedia is not supported by your browser' },
				OcrLog.PRELOAD,
				LogType.Error,
			);
			this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
		}
		return result;
	}
	private getAnalysisViewportAspect(): number {
		const previewEl = this.video ?? this.canvas;
		if (!previewEl) return 1;
		const rect = previewEl.getBoundingClientRect();
		if (rect.width > 0 && rect.height > 0) {
			return rect.width / rect.height;
		}
		if (this.video?.videoWidth && this.video?.videoHeight) {
			return this.video.videoWidth / this.video.videoHeight;
		}
		return 1;
	}

	/*
	 * Controles de posición del documento
	 */
	private isMatch(): void {
		const MIN_NUM_IMAGES = 24;
		const PERCENTAGE_TO_MATCH = 70;
		this.positionDocumentService.drawControl(this.ctx);
		this.positionDocumentService.setControlRect(this.canvas, this.getAnalysisViewportAspect());
		this.fullDataMatch.push(this.positionDocumentService.isMatch());
		if (
			this.positionDocumentService.isMatch() &&
			(this.fullDataMatch.length === MIN_NUM_IMAGES / 3 || this.fullDataMatch.length === (2 * MIN_NUM_IMAGES) / 3)
		) {
			this.ocrCannyConf.document.photos.push({
				img: this.canvas.toDataURL('image/jpeg', quality),
				lapacian: 0,
				fourier: 0,
				label: this.getCurrentDeviceLabel(),
				docSize: null,
				gridAnalysisBrightness: null,
				consecutiveCellsWith95: null,
				consecutiveCellsWith98: null,
				selected: false,
			});
		}
		if (this.fullDataMatch.length > MIN_NUM_IMAGES) {
			this.match =
				this.fullDataMatch.filter(Boolean).length > (this.fullDataMatch.length * PERCENTAGE_TO_MATCH) / 100;
			if (!this.match) {
				this.ocrCannyConf.document.photos = [];
			}
			this.fullDataMatch = [];
		}
	}

	/*
	 * Nav Help
	 */
	public navHelpChange(event: INavHelp): void {
		const actions = {
			[NavHelpAction.ROTATE]: () => this.changeCamera(),
			[NavHelpAction.QR]: () => (this.isVisibleScan = true),
			[NavHelpAction.HELP]: () => (this.isVisibleHelp = true),
			[NavHelpAction.LANGUAGE]: () => this.languagePickerUi.open(),
			[NavHelpAction.VOLUMEN]: () => this.navHelpService.toggleAudioGuide(event),
		};
		return actions[event.id]();
	}

	/*
	 * Funciones de la cámara
	 */
	/** Lista ya normalizada por CameraService (deviceId + dedupe). */
	private applySwitchableCamerasFromService(cameras: MediaDeviceInfo[]): void {
		this.allCameras = cameras;

		if (this.controls) {
			this.controls = {
				...this.controls,
				mediaDevices: this.allCameras,
			};
		} else {
			this.controls = {
				mediaDevices: this.allCameras,
			};
		}
		const foundIndex = this.navsHelp.findIndex((nav: INavHelp) => nav.id === NavHelpAction.ROTATE);
		if (this.allCameras && this.allCameras.length > 1) {
			if (foundIndex === -1) {
				this.navsHelp = this.navHelpService.getNavsHelpWithCamera();
			}
		} else {
			if (foundIndex > -1) {
				this.navsHelp.splice(foundIndex, 1);
			}
		}
		const foundCamera = this.allCameras.find((md: MediaDeviceInfo) => md.deviceId === this.getCurrentDeviceId());
		if (!foundCamera && this.allCameras?.length) {
			this.changeCamera();
		}
	}

	public async changeCamera(): Promise<void> {
		onCameraPreviewSwitchStarted({
			activatePreviewLoading: (key) => this.activatePreviewLoading(key),
			hidePreviewLoading: () => this.hidePreviewLoading(),
		});
		try {
			// Detener el stream actual si ya hay uno activo
			const availableCameras = this.allCameras.filter(
				(md: MediaDeviceInfo) => md.deviceId !== this.getCurrentDeviceId(),
			);
			if (this.stream && availableCameras.length) {
				cancelAnimationFrame(this.animationFrame);
				this.stream.getTracks().forEach((track) => track.stop());
			} else {
				this.hidePreviewLoading();
				return;
			}

			const currentIndex = this.allCameras.findIndex(
				(md: MediaDeviceInfo) => md.deviceId === this.getCurrentDeviceId(),
			);
			if (currentIndex > -1) {
				const newCamera: MediaDeviceInfo = this.allCameras[currentIndex + 1]
					? this.allCameras[currentIndex + 1]
					: this.allCameras[0];
				// Obtener el stream de la cámara seleccionada
				const stream = await navigator.mediaDevices.getUserMedia({
					video: {
						deviceId: {
							exact: newCamera.deviceId,
						},
						width: { ideal: 800 },
						height: { ideal: 600 },
						aspectRatio: 4 / 3,
					},
				});

				this.stream = stream;
				this.video.srcObject = stream; // Conectar el stream al video
				await waitForVideoPreviewReady(this.video);
				this.hidePreviewLoading();
			}
		} catch (error) {
			this.hidePreviewLoading();
			this.permissionsCamera = false;
			this.cloudLogsService.sendCloudLogs({ changeCamera: error?.toString() }, OcrLog.PRELOAD, LogType.Error);
			console.error('Error starting camera:', error);
		}
	}

	private getCurrentDeviceId(): string | null {
		const videoTrack = this.stream?.getVideoTracks()[0]; // Obtener la primera pista de video
		if (videoTrack) {
			return videoTrack.getSettings().deviceId; // Obtener el deviceId de la pista
		}
		return null;
	}

	private getCurrentDeviceLabel(): string {
		const currentIndex = this.allCameras.findIndex(
			(md: MediaDeviceInfo) => md.deviceId === this.getCurrentDeviceId(),
		);
		if (currentIndex > -1) {
			return this.allCameras[currentIndex].label;
		}
		return '';
	}

	/*
	 * Timer
	 */
	public startTest(): void {
		this.startTotalTime();
	}

	private resetTimers(): void {
		if (this.subscriptionTimers) {
			this.subscriptionTimers.unsubscribe();
			this.subscriptionTimers = new Subscription();
		}
	}

	// Total time
	private startTotalTime(): void {
		if (!this.canRunTestTimers()) return;
		if (!this.subsTotalTime) {
			this.subsTotalTime = new Subscription();
			this.subsTotalTime.add(
				interval(1000).subscribe(() => {
					this.totalTime = this.totalTime + 1;
				}),
			);
		}
	}

	private stopTotalTime(): void {
		if (this.subsTotalTime) {
			this.subsTotalTime.unsubscribe();
			this.subsTotalTime = null;
		}
	}

	// Warning time
	private startWarningTime(): void {
		if (!this.canRunTestTimers()) return;
		if (!this.subsWarningTime) {
			this.subsWarningTime = new Subscription();
			this.subsWarningTime.add(
				interval(1000).subscribe(() => {
					this.warningTime = this.warningTime + 1;
					if (this.warningTime >= OCR_WARNING_TIME) {
						this.stopWarningTime();
						this.ocrCannyConf.general.status = TestStatus.OnFail;
						this.sendResult(false, TEST_STATUS.KO);
					}
				}),
			);
		}
	}

	public stopWarningTime(): void {
		if (this.subsWarningTime) {
			this.subsWarningTime.unsubscribe();
			this.subsWarningTime = null;
		}
		this.warningTime = 0;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private setOcrSetting(setting: IOcrSetting): IOcrCannyConf {
		return this.operation.type === OPERATION_TYPE.OCR_CANNY ? ocrBackConfWithOutModels : ocrFrontConfWithOutModels;
	}

	private activatePreviewLoading(textKey: string): void {
		this.loading = {
			show: true,
			text: this.trans.instant(textKey),
		};
	}

	private hidePreviewLoading(): void {
		this.loading = { show: false, text: '' };
	}

	ngOnDestroy(): void {
		this.cameraPermissionsTimerService.stopWait();
		navigator.mediaDevices.removeEventListener('devicechange', this.onDeviceChangeRefreshCameras);
		this.destroy$.next();
		this.destroy$.complete();
		this.stopTotalTime();
		this.subscriptionTimers.unsubscribe();
	}
}
